import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, BarChart3 } from 'lucide-react';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { JobPosting, JobFormData, FormField, defaultFormFields } from './types';
import JobList from './JobList';
import JobCreationDialog from './JobCreationDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import JobApplicationsModal from './JobApplicationsModal';
import JobApplicationFormBuilder from './JobApplicationFormBuilder';
import SimpleRecruitmentAnalytics from './SimpleRecruitmentAnalytics';

interface UnifiedRecruitmentTabProps {
  businessId: string;
}

const UnifiedRecruitmentTab: React.FC<UnifiedRecruitmentTabProps> = ({ businessId }) => {
  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [loading, setLoading] = useState({
    fetchingJobs: true,
    savingJob: false,
    fetchingAnalytics: false
  });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<JobPosting | null>(null);
  const [applications, setApplications] = useState<{[key: string]: number}>({});
  const [selectedJobForApplications, setSelectedJobForApplications] = useState<JobPosting | null>(null);
  const [isFormBuilderOpen, setIsFormBuilderOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [applicationFormFields, setApplicationFormFields] = useState<FormField[]>([]);
  const [deleteConfirmJob, setDeleteConfirmJob] = useState<JobPosting | null>(null);

  const [formData, setFormData] = useState<JobFormData>({
    title: '',
    description: '',
    requirements: '',
    location: '',
    employment_type: 'full-time',
    salary_min: '',
    salary_max: '',
    hourly_rate_min: '',
    hourly_rate_max: '',
    benefits: '',
    schedule_requirements: '',
    experience_level: 'entry-level',
    expires_at: '',
    // Contract management fields
    requires_contract: false,
    default_contract_template_id: '',
    contract_auto_generate: false
  });

  useEffect(() => {
    fetchJobPostings();
    setApplicationFormFields(defaultFormFields);
  }, [businessId]);

  const fetchJobPostings = async () => {
    try {
      const { data, error } = await supabase
        .from('job_postings')
        .select(`
          *,
          qr_codes!qr_codes_job_posting_id_fkey(
            id,
            name,
            unique_url_path,
            url,
            type
          )
        `)
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobPostings(data || []);

      if (data && data.length > 0) {
        const jobIds = data.map(job => job.id);
        const { data: appCounts, error: countError } = await supabase
          .from('job_applications')
          .select('job_posting_id')
          .in('job_posting_id', jobIds);

        if (!countError && appCounts) {
          const counts: {[key: string]: number} = {};
          appCounts.forEach(app => {
            counts[app.job_posting_id] = (counts[app.job_posting_id] || 0) + 1;
          });
          setApplications(counts);
        }
      }
    } catch (error: unknown) {
      console.error('Error fetching job postings:', error);
      toast.error("Failed to fetch job postings. Please try again.");
    } finally {
      setLoading(prev => ({ ...prev, fetchingJobs: false }));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      requirements: '',
      location: '',
      employment_type: 'full-time',
      salary_min: '',
      salary_max: '',
      hourly_rate_min: '',
      hourly_rate_max: '',
      benefits: '',
      schedule_requirements: '',
      experience_level: 'entry-level',
      expires_at: '',
      requires_contract: false,
      default_contract_template_id: '',
      contract_auto_generate: false
    });
    setEditingJob(null);
    setCurrentStep(1);
    setApplicationFormFields(defaultFormFields);
  };

  const handleEditJob = (job: JobPosting) => {
    setFormData({
      title: job.title,
      description: job.description || '',
      requirements: job.requirements || '',
      location: job.location || '',
      employment_type: job.employment_type,
      salary_min: job.salary_min?.toString() || '',
      salary_max: job.salary_max?.toString() || '',
      hourly_rate_min: job.hourly_rate_min?.toString() || '',
      hourly_rate_max: job.hourly_rate_max?.toString() || '',
      benefits: (job as any).benefits || '',
      schedule_requirements: (job as any).schedule_requirements || '',
      experience_level: job.experience_level,
      expires_at: job.expires_at ? new Date(job.expires_at).toISOString().split('T')[0] : '',
      requires_contract: (job as any).requires_contract || false,
      default_contract_template_id: (job as any).default_contract_template_id || '',
      contract_auto_generate: (job as any).contract_auto_generate || false
    });
    setEditingJob(job);
    setCurrentStep(1);
    setApplicationFormFields((job as any).application_form_config || defaultFormFields);
    setIsCreateDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (currentStep === 1) {
      if (!formData.title.trim()) {
        toast.error("Job title is required.");
        return;
      }
      setCurrentStep(2);
      return;
    }

    setLoading(prev => ({ ...prev, savingJob: true }));

    try {
      const jobData = {
        business_id: businessId,
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        requirements: formData.requirements.trim() || null,
        location: formData.location.trim() || null,
        employment_type: formData.employment_type,
        salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null,
        hourly_rate_min: formData.hourly_rate_min ? parseFloat(formData.hourly_rate_min) : null,
        hourly_rate_max: formData.hourly_rate_max ? parseFloat(formData.hourly_rate_max) : null,
        benefits: formData.benefits.trim() || null,
        schedule_requirements: formData.schedule_requirements.trim() || null,
        experience_level: formData.experience_level,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null,
        application_form_config: applicationFormFields,
        is_active: true,
        requires_contract: formData.requires_contract,
        default_contract_template_id: formData.requires_contract && formData.default_contract_template_id ? formData.default_contract_template_id : null,
        contract_auto_generate: formData.requires_contract && formData.contract_auto_generate
      };

      if (editingJob) {
        const { error } = await supabase
          .from('job_postings')
          .update(jobData)
          .eq('id', editingJob.id);

        if (error) throw error;
        toast.success("Job posting has been updated successfully.");
      } else {
        const { data: insertedJob, error } = await supabase
          .from('job_postings')
          .insert(jobData)
          .select()
          .single();

        if (error) throw error;

        // Automatically create QR code for new job posting
        const uniqueUrlPath = `${Date.now()}-${businessId.substring(0, 8)}`;
        const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
        const qrCodeUrl = `${baseUrl}/job-details?code=${uniqueUrlPath}`;
        
        const { error: qrError } = await supabase
          .from('qr_codes')
          .insert({
            business_id: businessId,
            name: `${formData.title} - Job Application`,
            type: 'job-posting',
            unique_url_path: uniqueUrlPath,
            url: qrCodeUrl,
            job_posting_id: insertedJob.id
          });

        if (qrError) {
          console.error('Error creating QR code:', qrError);
          // Don't fail the job creation if QR code creation fails
        }

        toast.success("Job posting and QR code have been created successfully.");
      }

      setIsCreateDialogOpen(false);
      resetForm();
      fetchJobPostings();
    } catch (error: unknown) {
      console.error('Error saving job posting:', error);
      toast.error("Failed to save job posting. Please try again.");
    } finally {
      setLoading(prev => ({ ...prev, savingJob: false }));
    }
  };

  const toggleJobStatus = async (job: JobPosting) => {
    try {
      const { error } = await supabase
        .from('job_postings')
        .update({ is_active: !job.is_active })
        .eq('id', job.id);

      if (error) throw error;
      toast.success(`Job posting has been ${job.is_active ? 'deactivated' : 'activated'}.`);
      fetchJobPostings();
    } catch (error: unknown) {
      console.error('Error toggling job status:', error);
      toast.error("Failed to update job status. Please try again.");
    }
  };

  const confirmDeleteJob = async () => {
    if (!deleteConfirmJob) return;

    try {
      const { error } = await supabase
        .from('job_postings')
        .delete()
        .eq('id', deleteConfirmJob.id);

      if (error) throw error;
      toast.success("Job posting has been deleted successfully.");
      setDeleteConfirmJob(null);
      fetchJobPostings();
    } catch (error: unknown) {
      console.error('Error deleting job posting:', error);
      toast.error("Failed to delete job posting. Please try again.");
    }
  };

  return (
    <>
      <div className="space-y-8">
        {/* Job Postings Section */}
        <JobList
          jobPostings={jobPostings}
          applications={applications}
          onCreateJob={() => setIsCreateDialogOpen(true)}
          onEditJob={handleEditJob}
          onDeleteJob={setDeleteConfirmJob}
          onViewApplications={setSelectedJobForApplications}
          onToggleStatus={toggleJobStatus}
          onEditForm={(job) => {
            setEditingJob(job);
            setIsFormBuilderOpen(true);
          }}
          loading={loading.fetchingJobs}
        />

        {/* Analytics Section - Show basic metrics */}
        <div className="space-y-6">
          <div className="border-t pt-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">Recruitment Overview</h3>
            </div>
            <SimpleRecruitmentAnalytics 
              businessId={businessId}
              loading={loading.fetchingAnalytics}
            />
          </div>
        </div>
      </div>

      <JobCreationDialog
        isOpen={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          resetForm();
        }}
        currentStep={currentStep}
        editingJob={editingJob}
        formData={formData}
        setFormData={setFormData}
        applicationFormFields={applicationFormFields}
        setApplicationFormFields={setApplicationFormFields}
        onSubmit={handleSubmit}
        onStepBack={() => setCurrentStep(1)}
        loading={loading.savingJob}
        businessId={businessId}
      />

      <DeleteConfirmationDialog
        isOpen={!!deleteConfirmJob}
        job={deleteConfirmJob}
        applicationCount={applications[deleteConfirmJob?.id || ''] || 0}
        onConfirm={confirmDeleteJob}
        onCancel={() => setDeleteConfirmJob(null)}
      />

      <JobApplicationsModal
        isOpen={!!selectedJobForApplications}
        onClose={() => setSelectedJobForApplications(null)}
        jobPostingId={selectedJobForApplications?.id || ''}
        jobTitle={selectedJobForApplications?.title || ''}
        businessId={businessId}
      />

      <JobApplicationFormBuilder
        isOpen={isFormBuilderOpen}
        onClose={() => {
          setIsFormBuilderOpen(false);
          setEditingJob(null);
        }}
        jobPosting={editingJob}
      />
    </>
  );
};

export default UnifiedRecruitmentTab;